<template lang="pug">
	.creative-banner.flex-col.space-large
		Loader(v-if="isLoading", :fullscreen="true")
		FileUploader(ref="", :text="$tCaption('Upload')", icon="fas fa-cloud-arrow-up" :multiple="true", :accept="['.jpg', '.jpeg', '.png', '.bmp', '.gif']", @upload="fileChanged")
		.flex-col.space
			.banner.flex-col.space-medium.flex-1(v-for="(banner, idx) in banners", :class="{ 'disabled-controls': banner.__isDeleting }")
				.flex-col.space
					.flex-row.space.flex-1
						.flex-col.field.flex-1
							.font-small.upper.grey {{$tCaption('File name')}}
							DxTextBox(:value.sync="banner.FileName", @value-changed="createUpdateBanner(banner)")
						.flex-col.field.flex-1
							.font-small.upper.grey {{$tCaption('Alternate text')}}
							DxTextBox(:value.sync="banner.AlternateText", @value-changed="createUpdateBanner(banner)")
						.flex-col.field.flex-1
							.font-small.upper.grey {{$tCaption('Clickout link')}}
							DxTextBox(:value.sync="banner.ClickoutLink", @value-changed="createUpdateBanner(banner)")
					.flex-row
						.image(v-if="banner.Picture && banner.Picture.AzureFileUrl", :style="{ '--image-url': `url('${banner.Picture.AzureFileUrl}')` }")
							img(:src="banner.Picture.AzureFileUrl")
							.sizes(v-if="banner.Width && banner.Height") {{$tCaption('Size')}}: {{`${banner.Width} x ${banner.Height}`}}
					.flex-row.top-right-floated
						DxButton(icon="close", @click="deleteBanner(banner, idx)")
				.flex-col.space
					.flex-row.align-center.space
						.font-small.upper.grey {{$tCaption('Pixels')}}
						DxButton(icon="plus", stylingMode="text", @click="addBannerPixel(banner)")
					.flex-col.space
						.pixel.flex-col.field.flex-1(v-for="(pixel, idx) in banner.pixels", :class="{ 'disabled-controls': pixel.__isLoading }")
							.font-small.upper.grey {{$tCaption('Url') + ' #' + (idx + 1)}}
							DxTextBox(:value.sync="pixel.Url", @value-changed="createUpdateBannerPixel(banner, pixel, idx)")

</template>

<script>
	import Loader from 'app-core/Loader.vue';
	import FileUploader from 'app-core/FileUploader.vue';
	import _ from 'lodash';
	import DxTextBox from 'devextreme-vue/text-box';
	import DxButton from 'devextreme-vue/button';

	export default {
		translationCategory: 'creative.CreativeBanner',
		name: 'creative-audio-banner',
		components: { Loader, FileUploader, DxTextBox, DxButton },
		props: ['creativeId'],
		data() {
			return {
				banners: [],
				isLoading: false
			};
		},
		mounted() {
			if (__DEBUG__)
				window.creativeBanner = this;
			this.loadBanners();
		},
		methods: {
			createUpdateBannerPixel(banner, pixel, idx) {
				if (pixel.Url) {
					this.$set(pixel, '__isLoading', (pixel?.__isLoading || 0) + 1);
					this.$Data.postExternalApi('BannerPixel/CreateUpdateBannerPixel', pixel)
						.then(r => {
							if (!pixel.Oid) {
								pixel.Oid === r?.Data?.Oid;
							}
						})
						.finally(() => pixel.__isLoading--);
				} else {
					if (pixel.Oid) {
						banner.pixels.splice(idx, 1);
						this.$Data.getExternalApi('BannerPixel/DeleteBannerPixel', `id=${pixel.Oid}`);
					}
				}
			},
			loadBannerPixels(banner) {
				this.$set(banner, '__isLoading', (banner?.__isLoading || 0) + 1);
				this.$Data.getExternalApi('BannerPixel/GetAllBannerPixels', `bannerId=${banner.Oid}`, 999).then(r => {
					this.$set(banner, 'pixels', r?.Results || []);
				}).finally(() => banner.__isLoading--);
			},
			addBannerPixel(banner) {
				if (!banner.pixels) {
					this.$set(banner, 'pixels', []);
				}
				banner.pixels.push({
					Oid: null,
					Url: null,
					Banner: {
						Oid: banner?.Oid
					}
				});
			},
			deleteBanner(banner, idx) {
				this.$confirmMessage({
					title: this.$tCaption('Confirm action', '@actions'),
					text: this.$tCaption('Are you sure you want to delete the banner?'),
					handler: () => {
						this.isLoading++;
						banner.__isDeleting = true;
						this.$Data.getExternalApi('Banner/DeleteBanner', `id=${banner?.Oid}`)
							.then(() => this.banners.splice(idx, 1))
							.finally(() => {
								this.isLoading--;
								banner.__isDeleting = false;
							});
					}
				});
			},
			loadBanners() {
				this.isLoading++;
				this.$Data.getExternalApi('Banner/GetAllBanners', `creativeId=${this.creativeId}`, 999)
					.then(r => {
						this.banners = r?.Results || [];
						this.banners.map(f => this.loadBannerPixels(f));
					})
					.finally(() => this.isLoading--);
			},
			fileChanged(files) {
				if (files?.length) {
					this.isLoading++;
					Promise.all(files.map(f => this.createUpdateBanner({ Oid: null, Creative: { Oid: this.creativeId }, FileName: f.name }, f).then((banner) => {
						this.banners.push(banner);
					}))).finally(() => this.isLoading--);
				}
			},
			async createUpdateBanner(banner, file) {
				this.$set(banner, '__isLoading', (banner?.__isLoading || 0) + 1);
				const base64 = file && (await (new Promise(res => {
					var reader = new FileReader();
					reader.onload = () => {
						res(reader.result);
					}
					reader.readAsDataURL(file);
					return reader;
				})));

				if (base64) {
					await new Promise(res => {
						const img = new Image();
						img.onload = function() {
							banner.Width = this.width;
							banner.Height = this.height;
							res(banner);
						}
						img.src = base64;
						banner.Picture = {
							AzureFileUrl: base64
						}

						return img;
					});
				}

				return this.$Data.postExternalApi('Banner/CreateUpdateBanner', banner).then((r) => {
					if (r?.Success !== false) {
						_.assign(banner, r?.Data);
						if (base64) {
							banner.Picture = {
								AzureFileUrl: base64
							}
						}
					}
					if (base64) {
						const content = base64.split(';base64,')[1];
						let nameArr = `${file.name}`.split('.');
						banner.__isLoading--;
						this.$Data.postExternalApi('Banner/SaveImageToBanner', {
							ObjectId: banner.Oid,
							FileExtension: nameArr[nameArr.length-1],
							FileBase64Content: content
						}).finally(() => banner.isLoading--);
					}

					return banner;
				}).finally(() => banner.isLoading--);
			}
		}
	};
</script>
<style lang='scss'>
	.creative-banner {
		.banner {
			background: #fff;
			box-shadow: 0px 1px 4px rgb(0,0,0,.1);
			position: relative;
			padding: 20px;
			border-radius: 2px;

			.top-right-floated {
				position: absolute;
				z-index: 1;
				right: 2px;
				top: 2px;
			}

			.image {
				background: #dedede;
				position: relative;
				min-width: 50px;
				min-height: 50px;

				img {
					max-height: 280px;
					max-width: 640px;
				}
			}

			.sizes {
				position: absolute;
				top: 2px;
				right: 2px;
				padding: 4px;
				background: #fff;
				border-radius: 2px;
				font-size: 12px;
			}
		}
	}
</style>
